import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { Upload, File, X, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

// Use environment variable for API URL
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

console.log('🌐 DocumentUpload using API URL:', API);

interface UploadProgress {
  status: 'uploading' | 'complete' | 'error' | 'success';
  progress: number;
}

interface DocumentUploadProps {
  caseId: string;
  phaseId?: string;
  onUploaded?: () => void;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({ caseId, phaseId, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    
    console.log('🔍 Upload starting:', {
      apiUrl: API,
      caseId: caseId,
      phaseId: phaseId,
      filesCount: acceptedFiles.length
    });
    
    const token = auth.getToken();
    
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadUrl = `${API}/upload-document?case_id=${caseId}${phaseId ? `&phase_id=${phaseId}` : ''}`;
        console.log('📤 Uploading to:', uploadUrl);
        
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'uploading', progress: 0 }
        }));
        
        const response = await axios.post(
          uploadUrl,
          formData,
          {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({
                ...prev,
                [file.name]: { status: 'uploading', progress: percentCompleted }
              }));
            }
          }
        );
        
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'success', progress: 100 }
        }));
        
        toast.success(`${file.name} lastet opp!`);
        
        if (onUploaded) onUploaded();
        
      } catch (error: any) {
        console.error('❌ Upload error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
          headers: error.config?.headers
        });
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { status: 'error', progress: 0 }
        }));
        
        // Show more specific error message - handle object errors properly
        let errorMsg = 'Ukjent feil';
        if (error.response?.data?.detail) {
          errorMsg = typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : JSON.stringify(error.response.data.detail);
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = error.message;
        }
        toast.error(`Kunne ikke laste opp ${file.name}: ${errorMsg}`);
      }
    }
    
    setUploading(false);
    
    // Clear progress after 3 seconds
    setTimeout(() => setUploadProgress({}), 3000);
  }, [caseId, phaseId, onUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    multiple: true
  });

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 transition-all cursor-pointer ${
          isDragActive
            ? 'border-purple-500 bg-purple-50'
            : 'border-gray-300 hover:border-purple-400 bg-gray-50 hover:bg-purple-50/50'
        }`}
      >
        <input {...getInputProps()} />
        
        <div className="text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            isDragActive ? 'bg-purple-600' : 'bg-gray-200'
          }`}>
            <Upload className={`w-8 h-8 ${isDragActive ? 'text-white' : 'text-gray-600'}`} />
          </div>
          
          {isDragActive ? (
            <p className="text-lg font-medium text-purple-600">Slipp dokumentene her...</p>
          ) : (
            <>
              <p className="text-lg font-medium text-gray-900 mb-2">
                Dra og slipp dokumenter her
              </p>
              <p className="text-sm text-gray-600 mb-4">
                eller klikk for å velge filer
              </p>
              <p className="text-xs text-gray-500">
                Støtter: PDF, DOCX, TXT
              </p>
            </>
          )}
        </div>
      </div>
      
      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([filename, progress]) => (
            <div key={filename} className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  progress.status === 'success' ? 'bg-emerald-100' :
                  progress.status === 'error' ? 'bg-red-100' :
                  'bg-blue-100'
                }`}>
                  {progress.status === 'success' ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : progress.status === 'error' ? (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  ) : (
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{filename}</p>
                  {progress.status === 'uploading' && (
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                        style={{ width: `${progress.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
